import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faAngleDoubleUp, faQuestion, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BButton, BModal, BPopover } from "bootstrap-vue";
import { kebabCase } from "lodash";
import { computed, ref, set } from "vue";
import { getOperatorForAlias } from "@/utils/filtering";
import DelayedInput from "@/components/Common/DelayedInput.vue";
import FilterMenuBoolean from "@/components/Common/FilterMenuBoolean.vue";
import FilterMenuDropdown from "@/components/Common/FilterMenuDropdown.vue";
import FilterMenuInput from "@/components/Common/FilterMenuInput.vue";
import FilterMenuMultiTags from "@/components/Common/FilterMenuMultiTags.vue";
import FilterMenuObjectStore from "@/components/Common/FilterMenuObjectStore.vue";
import FilterMenuRanged from "@/components/Common/FilterMenuRanged.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'FilterMenu',
    props: {
        name: { default: "Menu" },
        placeholder: { default: "search for items" },
        debounceDelay: { default: 500 },
        filterClass: null,
        filterText: { default: "" },
        hasHelp: { type: Boolean },
        hasClearBtn: { type: Boolean },
        loading: { type: Boolean },
        menuType: { default: "linked" },
        searchError: { default: undefined },
        showAdvanced: { type: Boolean, default: false },
        view: { default: "dropdown" }
    },
    emits: ["update:filter-text", "update:show-advanced", "on-search"],
    setup(__props, { emit }) {
        const props = __props;
        library.add(faAngleDoubleUp, faQuestion, faSearch);
        const validFilters = computed(() => props.filterClass.validFilters);
        const filters = computed(() => Object.fromEntries(props.filterClass.getFiltersForText(props.filterText)));
        const identifier = kebabCase(props.name);
        const advancedMenu = ref(null);
        const delayedInputField = ref(null);
        const toggleMenuButton = computed(() => {
            const element = delayedInputField.value?.$el.querySelector(`[data-description='toggle advanced search']`);
            return element;
        });
        // Boolean for showing the help modal for the whole filter menu (if provided)
        const showHelp = ref(false);
        const isDisabled = ref({});
        const formattedSearchError = computed(() => {
            if (props.searchError) {
                const { column, col, operation, op, value, val, err_msg, ValueError } = props.searchError;
                const alias = operation || op;
                const operator = alias ? getOperatorForAlias(alias) : ":";
                const formatted = {
                    filter: `${column || col}${operator}`,
                    index: alias && alias !== "eq" ? `${column || col}_${alias}` : column || col,
                    value: value || val,
                    msg: err_msg,
                    typeError: ValueError,
                };
                return formatted;
            }
            else {
                return null;
            }
        });
        // computed ref with a get and set function
        const localAdvancedToggle = computed({
            get: () => props.showAdvanced,
            set: (value) => {
                emit("update:show-advanced", value);
            },
        });
        /** Returns the `typeError` or `msg` for a given `field` */
        function errorForField(field) {
            if (formattedSearchError.value && formattedSearchError.value?.index == field) {
                return formattedSearchError.value.typeError || formattedSearchError.value.msg;
            }
            return "";
        }
        /** Returns the `ValidFilter<any>` for given `filter`
         *
         * This non-null asserts the output because where it's used, the filter is guaranteed
         * to exist because of the `<span v-if="validFilters[filter]?.menuItem">` condition
         * @param filter the filter to get
         * @returns the _defined_ `ValidFilter<any>` for the given `filter`
         */
        function getValidFilter(filter) {
            return validFilters.value[filter];
        }
        /** Explicitly sets a filter: value
         * (also closes help modal for this filter if it exists)
         * @param filter the filter to set
         * @param value the value to set it to
         */
        function onOption(filter, value) {
            filters.value[filter] = value;
            setDisabled(filter, value);
            // for the compact view, we want to immediately search
            if (props.view === "compact") {
                onSearch();
            }
        }
        function onPopoverShown() {
            advancedMenu.value?.querySelector("input")?.focus();
        }
        function onPopoverHidden() {
            toggleMenuButton.value?.focus();
        }
        function onSearch() {
            const newFilterText = props.filterClass.getFilterText(filters.value);
            const newBackendFilter = props.filterClass.getFilterText(filters.value, true);
            if (props.menuType !== "linked") {
                emit("on-search", filters.value, newFilterText, newBackendFilter);
            }
            else {
                updateFilterText(newFilterText);
                // for the compact view, we do not want to close the advanced menu
                if (props.view !== "compact") {
                    onToggle();
                }
            }
        }
        function onToggle() {
            emit("update:show-advanced", !props.showAdvanced);
        }
        function setDisabled(filter, newVal) {
            const disablesFilters = validFilters.value[filter]?.disablesFilters;
            const type = validFilters.value[filter]?.type;
            if (disablesFilters && type !== Boolean) {
                for (const [disabledFilter, disablingValues] of Object.entries(disablesFilters)) {
                    if (newVal && (disablingValues === null || disablingValues.includes(newVal))) {
                        set(isDisabled.value, disabledFilter, true);
                        filters.value[disabledFilter] = undefined;
                    }
                    else {
                        set(isDisabled.value, disabledFilter, false);
                    }
                }
            }
        }
        function updateFilterText(newFilterText) {
            emit("update:filter-text", newFilterText);
        }
        return { __sfc: true, props, emit, validFilters, filters, identifier, advancedMenu, delayedInputField, toggleMenuButton, showHelp, isDisabled, formattedSearchError, localAdvancedToggle, errorForField, getValidFilter, onOption, onPopoverShown, onPopoverHidden, onSearch, onToggle, setDisabled, updateFilterText, faAngleDoubleUp, faQuestion, faSearch, FontAwesomeIcon, BButton, BModal, BPopover, DelayedInput, FilterMenuBoolean, FilterMenuDropdown, FilterMenuInput, FilterMenuMultiTags, FilterMenuObjectStore, FilterMenuRanged };
    }
});
